import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { dateInviteService } from '../../services/dateInviteService';
import Loader from '../../components/Loader';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';
import Pagination from '../../components/common/Pagination';
import ActionMenu from '../../components/common/ActionMenu';
import ConfirmModal from '../../components/common/ConfirmModal';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import DetailField from '../../components/common/DetailField';

const PUBLIC_BASE = process.env.REACT_APP_WEBSITE_URL || 'https://www.geekdas.in';

const ResponseBadge = ({ answeredAt }) => (
   <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
         answeredAt
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
      }`}
   >
      {answeredAt ? 'Yes' : 'Pending'}
   </span>
);

const DateInvites = () => {
   const [invites, setInvites] = useState([]);
   const [loading, setLoading] = useState(true);
   const [search, setSearch] = useState('');
   const [searchText, setSearchText] = useState('');
   const [page, setPage] = useState(1);
   const debounceRef = useRef(null);
   const [totalPages, setTotalPages] = useState(1);
   const [total, setTotal] = useState(0);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);
   const [viewTarget, setViewTarget] = useState(null);

   const fetchInvites = useCallback(async () => {
      try {
         setLoading(true);
         const result = await dateInviteService.getAll({ page, limit: 10, search });
         setInvites(result.invites);
         setTotalPages(result.totalPages);
         setTotal(result.total);
      } catch {
         toast.error('Failed to fetch date invites');
      } finally {
         setLoading(false);
      }
   }, [page, search]);

   useEffect(() => {
      fetchInvites();
   }, [fetchInvites]);

   const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchText(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
         setSearch(value);
         setPage(1);
      }, 400);
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await dateInviteService.delete(deleteTarget.id);
         toast.success('Date invite deleted');
         setDeleteTarget(null);
         fetchInvites();
      } catch {
         toast.error('Failed to delete date invite');
      } finally {
         setDeleting(false);
      }
   };

   const copyLink = (slug) => {
      navigator.clipboard.writeText(`${PUBLIC_BASE}/d/${slug}`);
      toast.success('Link copied to clipboard');
   };

   if (loading && invites.length === 0) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            <div>
               <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Date Invites</h1>
               <p className="mt-1 text-gray-600 dark:text-gray-400">
                  {total} invite{total !== 1 ? 's' : ''} total &middot; publicly created,
                  unmoderated content &mdash; delete anything abusive
               </p>
            </div>

            <div className="relative w-64">
               <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 dark:text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                     />
                  </svg>
               </span>
               <input
                  type="text"
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder="Search by question..."
                  className="pl-9 pr-3 min-h-[42px] border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-primary text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent focus:border-transparent text-sm w-full"
               />
            </div>

            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Question
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              After Message
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Response
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Views
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Created
                           </th>
                           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                        {loading ? (
                           <tr>
                              <td
                                 colSpan="6"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 Loading...
                              </td>
                           </tr>
                        ) : invites.length === 0 ? (
                           <tr>
                              <td
                                 colSpan="6"
                                 className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 No date invites yet
                              </td>
                           </tr>
                        ) : (
                           invites.map((invite) => (
                              <tr
                                 key={invite.id}
                                 className="hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                 <td
                                    className="px-6 py-4 cursor-pointer"
                                    onClick={() => setViewTarget(invite)}
                                 >
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs">
                                       {invite.question}
                                    </p>
                                 </td>
                                 <td
                                    className="px-6 py-4 cursor-pointer"
                                    onClick={() => setViewTarget(invite)}
                                 >
                                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                                       {invite.yesMessage}
                                    </p>
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <ResponseBadge answeredAt={invite.answeredAt} />
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {invite.viewCount}
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <DateTimeDisplay dateStr={invite.createdAt} />
                                 </td>
                                 <td className="px-6 py-4 whitespace-nowrap">
                                    <ActionMenu
                                       items={[
                                          {
                                             label: 'View details',
                                             onClick: () => setViewTarget(invite),
                                          },
                                          {
                                             label: 'Copy link',
                                             onClick: () => copyLink(invite.slug),
                                          },
                                          {
                                             label: 'Open',
                                             onClick: () =>
                                                window.open(
                                                   `${PUBLIC_BASE}/d/${invite.slug}`,
                                                   '_blank',
                                                   'noopener,noreferrer'
                                                ),
                                          },
                                          {
                                             label: 'Delete',
                                             variant: 'danger',
                                             onClick: () => setDeleteTarget(invite),
                                          },
                                       ]}
                                    />
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>
            </div>

            <Pagination
               page={page}
               totalPages={totalPages}
               total={total}
               limit={10}
               onPageChange={setPage}
            />
         </div>

         <Modal
            isOpen={!!viewTarget}
            onClose={() => setViewTarget(null)}
            title="Date Invite Details"
            maxWidth="max-w-2xl"
         >
            {viewTarget && (
               <div className="space-y-4">
                  <DetailField label="Question">{viewTarget.question}</DetailField>
                  <DetailField label="After Message">{viewTarget.yesMessage}</DetailField>
                  <div className="grid grid-cols-3 gap-4">
                     <DetailField label="Response">
                        <ResponseBadge answeredAt={viewTarget.answeredAt} />
                     </DetailField>
                     <DetailField label="Views">{viewTarget.viewCount}</DetailField>
                     <DetailField label="Created">
                        <DateTimeDisplay dateStr={viewTarget.createdAt} />
                     </DetailField>
                  </div>
                  <DetailField label="Link">{`${PUBLIC_BASE}/d/${viewTarget.slug}`}</DetailField>
                  <div className="flex justify-end pt-2">
                     <Button variant="secondary" onClick={() => setViewTarget(null)}>
                        Close
                     </Button>
                  </div>
               </div>
            )}
         </Modal>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Date Invite"
            message={`Are you sure you want to delete this invite ("${deleteTarget?.question}")? This cannot be undone.`}
            confirmLabel="Delete"
            confirmVariant="danger"
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default DateInvites;
